/**
 * PuntoControlEventoRepository - Observaciones de Puntos de Control
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 *
 * Serialización de `fotos`: en Supabase es JSONB (ARRAY de objetos) y viaja
 * así por la cola de sync, pero en los caches locales se guarda como STRING
 * JSON (SQLite no soporta objetos como valor de columna). Este repo
 * normaliza en el límite, igual que ElementoMapaRepository con `geometria`:
 *   - Escrituras: stringifica fotos para el adapter local, emite el cambio
 *     de sync con el ARRAY.
 *   - Lecturas: parsea el string de vuelta a array.
 */

import { v4 as uuidv4 } from 'uuid';
import {
  IPuntoControlEventoRepository,
  PuntoControlEvento,
  FotoEventoControl
} from '../interfaces/IPuntoControlRepository';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter, WhereClause } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';

/** Campos JSON que no son `fotos` (que tiene su propio manejo). */
const CAMPOS_JSON_OPCIONALES = ['plantas_checklist', 'cobertura', 'analisis_foto'] as const;

export class PuntoControlEventoRepository
  extends BaseRepository<PuntoControlEvento, string>
  implements IPuntoControlEventoRepository
{
  private readonly NOT_DELETED: WhereClause = { field: 'is_deleted', operator: 'equals', value: false };

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'puntos_control_eventos', 'puntos_control_eventos');

    this.supportsSoftDelete = true;
    this.autoTimestamp = true;

    if (profileRepo) {
      this.setProfileRepository(profileRepo);
    }
  }

  // ==================== Normalización fotos ====================

  /** Campos JSON (array/objeto en Supabase, string en el caché local). */
  private toStorage(record: Record<string, any>): Record<string, any> {
    const out = { ...record };
    if (Array.isArray(out.fotos)) out.fotos = JSON.stringify(out.fotos);
    for (const campo of CAMPOS_JSON_OPCIONALES) {
      if (out[campo] != null && typeof out[campo] !== 'string') out[campo] = JSON.stringify(out[campo]);
    }
    return out;
  }

  /** Campos JSON opcionales: string del caché local → array/objeto. */
  private parseCamposOpcionales<T>(row: T): T {
    if (!row) return row;
    const out: Record<string, any> = { ...row };
    for (const campo of CAMPOS_JSON_OPCIONALES) {
      if (typeof out[campo] === 'string') {
        try {
          out[campo] = JSON.parse(out[campo]);
        } catch {
          out[campo] = null;
        }
      }
    }
    return out as T;
  }

  /** fotos string JSON (o ya array) → array (para los consumidores). */
  private fromStorage<T extends { fotos?: any }>(row: T): T {
    row = this.parseCamposOpcionales(row);
    if (row && typeof row.fotos === 'string' && row.fotos) {
      try {
        return { ...row, fotos: JSON.parse(row.fotos) };
      } catch (e) {
        console.warn('[PuntoControlEventoRepository] fotos corrupta, se ignora:', e);
        return { ...row, fotos: [] };
      }
    }
    if (row && row.fotos === undefined) {
      return { ...row, fotos: [] };
    }
    return row;
  }

  private fromStorageList(rows: any[]): any[] {
    return rows.map(r => this.fromStorage(r));
  }

  // ==================== CRUD ====================

  async add(data: Omit<PuntoControlEvento, 'id'>): Promise<string> {
    const id = uuidv4();
    const now = this.getCurrentTimestamp();
    const profile = await this.getCurrentProfile();

    const fotos = Array.isArray(data.fotos) ? data.fotos : [];
    const record: PuntoControlEvento = {
      ...data,
      id,
      fotos,
      creado_por: data.creado_por || profile?.user_id || '',
      empresa_id: data.empresa_id || profile?.empresa_activa_id || '',
      created_at: data.created_at || now,
      updated_at: now,
      is_deleted: false
    };

    // Local: string; sync: array (JSONB en Supabase).
    await this.db.insert('puntos_control_eventos', this.toStorage(record));
    this.emitSync('INSERT', record);
    return id;
  }

  async update(data: Partial<PuntoControlEvento> & { id: string }): Promise<void> {
    const updatedData = {
      ...data,
      updated_at: this.getCurrentTimestamp()
    };
    await this.db.update('puntos_control_eventos', data.id, this.toStorage(updatedData as any));
    this.emitSync('UPDATE', updatedData as any);
  }

  /** Reemplaza las fotos de un evento (usado al subir una foto pendiente). */
  async updateFotos(id: string, fotos: FotoEventoControl[]): Promise<void> {
    await this.update({ id, fotos });
  }

  /** Soft-delete de una observación (mismo modelo que el resto del mapa). */
  async delete(id: string): Promise<void> {
    await this.softDelete(id);
  }

  /** Override del PULL: stringifica fotos antes del adapter local. */
  async bulkPutSilent(items: any[]): Promise<void> {
    await this.db.bulkUpsert('puntos_control_eventos', items.map(i => this.toStorage(i)));
    items.forEach(item => {
      this.emitLocalSync('UPDATE', this.fromStorage(item));
    });
  }

  async get(id: string): Promise<PuntoControlEvento | undefined> {
    const row = await this.db.get('puntos_control_eventos', id);
    if (!row) return undefined;
    return this.fromStorage(row as any) as PuntoControlEvento;
  }

  async getAll(): Promise<PuntoControlEvento[]> {
    const rows = ((await this.db.getAll('puntos_control_eventos')) as any[]) || [];
    return this.fromStorageList(rows.filter(r => !r.is_deleted)) as PuntoControlEvento[];
  }

  // ==================== Queries ====================

  /**
   * Eventos de un punto, más recientes primero. La ordenación es en
   * memoria: los eventos de un punto son decenas, no miles.
   */
  async getByPunto(puntoId: string): Promise<PuntoControlEvento[]> {
    const rows = await this.db.queryMultiple<PuntoControlEvento>('puntos_control_eventos', [
      this.NOT_DELETED,
      { field: 'punto_id', operator: 'equals', value: puntoId }
    ]);
    return this.fromStorageList(rows as any[])
      .sort((a: any, b: any) =>
        b.fecha_observacion.localeCompare(a.fecha_observacion) ||
        (b.created_at ?? '').localeCompare(a.created_at ?? '')
      ) as PuntoControlEvento[];
  }
}
